import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Plus,
  Store,
  Star,
  Edit,
  Trash2,
  Eye,
  MapPin,
  Settings,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMyBusinesses = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/my-businesses`, {
        headers,
        withCredentials: true
      });
      setBusinesses(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch businesses:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyBusinesses();
  }, [token]);

  const handleDelete = async (businessId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/businesses/${businessId}`, {
        headers,
        withCredentials: true
      });
      toast.success('Business deleted successfully');
      fetchMyBusinesses();
    } catch (error) {
      toast.error('Failed to delete business');
    }
  };

  return (
    <div className="min-h-screen bg-stone-white" data-testid="dashboard-page">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-display text-3xl font-bold text-stone-900" data-testid="dashboard-title">
                Welcome, {user?.name}
              </h1>
              <p className="text-stone-600 mt-1">
                Manage your business listings
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/account/settings">
                <Button variant="outline" className="rounded-full gap-2" data-testid="account-settings-btn">
                  <Settings className="w-4 h-4" />
                  Settings
                </Button>
              </Link>
              <Link to="/add-business">
                <Button className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full gap-2" data-testid="add-business-btn">
                  <Plus className="w-4 h-4" />
                  Add New Business
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <Store className="w-8 h-8 text-emerald-deep mb-3" />
              <div className="font-display text-2xl font-bold text-stone-900">
                {businesses.length}
              </div>
              <div className="text-stone-500 text-sm">Total Businesses</div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <Star className="w-8 h-8 text-legacy-gold mb-3" />
              <div className="font-display text-2xl font-bold text-stone-900">
                {businesses.reduce((sum, b) => sum + b.review_count, 0)}
              </div>
              <div className="text-stone-500 text-sm">Total Reviews</div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <Eye className="w-8 h-8 text-emerald-deep mb-3" />
              <div className="font-display text-2xl font-bold text-stone-900">
                {businesses.reduce((sum, b) => sum + b.average_rating, 0).toFixed(1)}
              </div>
              <div className="text-stone-500 text-sm">Avg. Rating</div>
            </div>
          </div>

          {/* Business List */}
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="p-6 border-b border-stone-200">
              <h2 className="font-display text-xl font-semibold text-stone-900">
                Your Businesses
              </h2>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-deep border-t-transparent mx-auto"></div>
              </div>
            ) : businesses.length > 0 ? (
              <div className="divide-y divide-stone-100" data-testid="business-list">
                {businesses.map((business) => (
                  <div
                    key={business.business_id}
                    className="p-6 flex flex-col sm:flex-row sm:items-center gap-4 hover:bg-stone-50 transition-colors"
                    data-testid={`dashboard-business-${business.business_id}`}
                  >
                    {/* Business Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-display text-lg font-semibold text-stone-900">
                          {business.name}
                        </h3>
                        <Badge variant="secondary" className="bg-stone-100 text-stone-600">
                          {business.category}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-stone-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {business.city}, {business.state}
                        </span>
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-legacy-gold" />
                          {business.average_rating.toFixed(1)} ({business.review_count} reviews)
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Link to={`/business/${business.business_id}`}>
                        <Button variant="ghost" size="sm" className="gap-1">
                          <Eye className="w-4 h-4" />
                          View
                        </Button>
                      </Link>
                      <Link to={`/edit-business/${business.business_id}`}>
                        <Button variant="ghost" size="sm" className="gap-1" data-testid={`edit-${business.business_id}`}>
                          <Edit className="w-4 h-4" />
                          Edit
                        </Button>
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1" data-testid={`delete-${business.business_id}`}>
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Business?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete "{business.name}" and all its reviews. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(business.business_id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center" data-testid="no-businesses">
                <Store className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <h3 className="font-display text-xl font-semibold text-stone-700 mb-2">
                  No Businesses Yet
                </h3>
                <p className="text-stone-500 mb-6">
                  Start by adding your first Black-owned business listing
                </p>
                <Link to="/add-business">
                  <Button className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full gap-2">
                    <Plus className="w-4 h-4" />
                    Add Your First Business
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DashboardPage;
